import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as hostelService from "../service/hostelService";
import type { HostelState } from "../types";
import i18n from "@/i18n";

export const fetchBuildings = createAsyncThunk("hostel/fetchBuildings", async (institutionId: number) => {
  return await hostelService.fetchBuildings(institutionId);
});

export const createBuilding = createAsyncThunk(
  "hostel/createBuilding",
  async ({ institutionId, payload }: { institutionId: number; payload: { name: string } }, { dispatch }) => {
    await hostelService.createBuilding(institutionId, payload);
    await dispatch(fetchBuildings(institutionId));
  }
);

export const fetchRooms = createAsyncThunk(
  "hostel/fetchRooms",
  async ({ institutionId, buildingId }: { institutionId: number; buildingId?: number }) => {
    return await hostelService.fetchRooms(institutionId, buildingId);
  }
);

export const createRoom = createAsyncThunk(
  "hostel/createRoom",
  async (
    { institutionId, payload }: { institutionId: number; payload: { buildingId: number; roomNumber: string; capacity?: number } },
    { dispatch }
  ) => {
    await hostelService.createRoom(institutionId, payload);
    await dispatch(fetchRooms({ institutionId }));
  }
);

export const allocate = createAsyncThunk(
  "hostel/allocate",
  async (
    { institutionId, payload }: { institutionId: number; payload: { roomId: number; studentId: number; academicYearId?: number } },
    { dispatch }
  ) => {
    await hostelService.allocate(institutionId, payload);
    await dispatch(fetchRooms({ institutionId }));
  }
);

export const vacate = createAsyncThunk(
  "hostel/vacate",
  async (
    { institutionId, allocationId, studentId, academicYearId }: { institutionId: number; allocationId: number; studentId: number; academicYearId?: number },
    { dispatch }
  ) => {
    await hostelService.vacate(institutionId, allocationId);
    await dispatch(fetchRooms({ institutionId }));
    await dispatch(fetchStudentAllocations({ institutionId, studentId, academicYearId }));
  }
);

export const fetchStudentAllocations = createAsyncThunk(
  "hostel/fetchStudentAllocations",
  async ({ institutionId, studentId, academicYearId }: { institutionId: number; studentId: number; academicYearId?: number }) => {
    return await hostelService.fetchStudentAllocations(institutionId, studentId, academicYearId);
  }
);

const initialState: HostelState = {
  buildings: [],
  rooms: [],
  studentAllocations: [],
  loading: false,
  saving: false,
  error: null,
};

const hostelSlice = createSlice({
  name: "hostel",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBuildings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBuildings.fulfilled, (state, action) => {
        state.loading = false;
        state.buildings = action.payload;
      })
      .addCase(fetchBuildings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToLoadBuildings");
      })
      .addCase(createBuilding.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToAddBuilding");
      })
      .addCase(fetchRooms.fulfilled, (state, action) => {
        state.rooms = action.payload;
      })
      .addCase(createRoom.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToAddRoom");
      })
      .addCase(allocate.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(allocate.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(allocate.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToAllocateStudent");
      })
      .addCase(vacate.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToVacateAllocation");
      })
      .addCase(fetchStudentAllocations.fulfilled, (state, action) => {
        state.studentAllocations = action.payload;
      });
  },
});

export default hostelSlice.reducer;
